import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Newspaper, Loader2, Send, ArrowLeft, Trash2, ChevronDown, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { API, PHOTO_OPTIONS } from "@/constants/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { title: "", body: "", category: "Announcement", date: "", image: "" };
const inputCls =
  "rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 h-auto text-base focus-visible:border-brand-sky focus-visible:ring-brand-sky/20 transition-colors";

export default function AdminNews() {
  const [posts, setPosts] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const token = sessionStorage.getItem("bca_admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/news`);
      setPosts(data);
    } catch {
      toast.error("Could not load posts.");
    }
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    load();
  }, [token, navigate, load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/news`,
        { ...form, date: form.date || null, image: form.image || null },
        { headers }
      );
      toast.success("News posted!");
      setForm(EMPTY);
      load();
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("bca_admin_token");
        navigate("/admin");
      } else {
        toast.error("Could not save the post.");
      }
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/news/${id}`, { headers });
      toast.success("Post deleted.");
      load();
    } catch {
      toast.error("Could not delete the post.");
    }
  };

  return (
    <main data-testid="admin-news-page" className="mx-auto max-w-5xl px-6 pb-24 pt-36">
      <Link to="/admin/leads" data-testid="news-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-brand-deepsky hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to all enquiries
      </Link>

      <div className="mt-6 flex items-center gap-4">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-coral/15 text-brand-deepcoral">
          <Newspaper className="h-7 w-7" />
        </span>
        <div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">News & Events</h1>
          <p className="text-sm text-slate-500">Post celebrations, achievements and announcements — they appear on the Home page instantly.</p>
        </div>
      </div>

      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        data-testid="news-form"
        className="mt-10 rounded-[2.5rem] border border-amber-200/50 bg-white p-7 md:p-10 shadow-[0_25px_60px_rgba(2,132,199,0.1)]"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Title *</span>
            <Input required data-testid="news-title" value={form.title} onChange={set("title")} placeholder="e.g. Annual Day Celebration" className={inputCls} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Category *</span>
            <div className="relative">
              <select data-testid="news-category" value={form.category} onChange={set("category")} className={`${inputCls} w-full appearance-none pr-10`}>
                {["Announcement", "Celebration", "Achievement"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Date</span>
            <Input type="date" data-testid="news-date" value={form.date} onChange={set("date")} className={inputCls} />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Photo (optional)</span>
            <div className="relative">
              <select data-testid="news-image" value={form.image} onChange={set("image")} className={`${inputCls} w-full appearance-none pr-10`}>
                {PHOTO_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
            {form.image && (
              <img src={form.image} alt="Selected" data-testid="news-image-preview" className="mt-3 h-28 w-44 rounded-2xl object-cover border-2 border-brand-sky/30" />
            )}
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Details *</span>
            <Textarea required data-testid="news-body" value={form.body} onChange={set("body")} placeholder="What happened, who participated, what parents should know..." rows={4} className={inputCls} />
          </label>
        </div>
        <motion.button
          type="submit"
          disabled={sending}
          data-testid="news-submit-btn"
          whileTap={{ scale: 0.97 }}
          className="mt-7 inline-flex min-h-[52px] w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-brand-coral px-10 font-bold text-white shadow-[0_10px_30px_rgba(251,113,133,0.4)] transition-transform duration-300 hover:scale-105 disabled:opacity-60"
        >
          {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
          {sending ? "Posting..." : "Post News"}
        </motion.button>
      </motion.form>

      <h2 className="mt-14 font-display text-2xl font-semibold">Published posts</h2>
      <div className="mt-6 space-y-4" data-testid="news-list">
        {(posts || []).map((p) => (
          <div key={p.id} data-testid={`news-item-${p.id}`} className="flex items-start justify-between gap-4 rounded-3xl border border-amber-200/50 bg-white p-6 shadow-sm">
            <div className="flex items-start gap-4">
              {p.image && (
                <img src={p.image} alt="" className="h-16 w-24 shrink-0 rounded-2xl object-cover border border-amber-200/60" />
              )}
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-brand-sky/15 px-3 py-1 text-xs font-bold text-brand-deepsky">{p.category}</span>
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-400">
                    <CalendarDays className="h-3.5 w-3.5" /> {p.date}
                  </span>
                </div>
                <h3 className="mt-2 font-display text-lg font-semibold">{p.title}</h3>
                <p className="mt-1 text-sm text-slate-600 line-clamp-2">{p.body}</p>
              </div>
            </div>
            <button
              onClick={() => remove(p.id)}
              data-testid={`news-delete-${p.id}`}
              aria-label="Delete post"
              className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-coral/10 text-brand-deepcoral transition-colors hover:bg-brand-coral hover:text-white"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        ))}
        {posts && posts.length === 0 && (
          <p className="rounded-3xl bg-white p-8 text-center text-sm font-semibold text-slate-400">No posts yet — add the first one above.</p>
        )}
      </div>
    </main>
  );
}
